import QuickModal from '../components/quick/QuickModal';
import { BsEye } from 'react-icons/bs';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import Select from 'react-select';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { apiFetch, parseJsonSafe } from '../utils/api';

const statusOptions = [
  { value: 'a_la_espera', label: 'A la espera' },
  { value: 'en_proceso', label: 'En proceso' },
  { value: 'finalizado', label: 'Finalizado' }
];
const tipoOptions = [
  { value: 'cliente', label: 'Cliente' },
  { value: 'interno', label: 'Interno' }
];
const prioridadOptions = [
  { value: 'alta', label: 'Alta' },
  { value: 'media', label: 'Media' },
  { value: 'baja', label: 'Baja' }
];

const getInitials = (name = '', email = '') => {
  const target = name || email || '';
  const parts = target.split(/\s+/).filter(Boolean);
  if (!parts.length) return (email || '?').slice(0, 2).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const daysToDate = (dateStr) => {
  if (!dateStr) return null;
  const target = new Date(dateStr);
  if (Number.isNaN(target.getTime())) return null;
  const today = new Date();
  const diff = Math.floor((target - today) / (1000 * 60 * 60 * 24));
  return diff;
};

const ProjectsPage = () => {
  const [previewProject, setPreviewProject] = useState(null);
  const token = localStorage.getItem('token');
  const location = useLocation();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [viewMode, setViewMode] = useState('kanban');
  const [showFinalizados, setShowFinalizados] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    clientId: '',
    estado: '',
    userId: '',
    fechaInicioDesde: '',
    fechaInicioHasta: '',
    fechaFinDesde: '',
    fechaFinHasta: ''
  });
  const [sort, setSort] = useState({ field: 'fecha_fin', order: 'asc' });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    titulo: '',
    client_id: null,
    estado: 'a_la_espera',
    fecha_inicio: '',
    fecha_fin: '',
    comentarios: '',
    assigned_user_ids: [],
    tipo: 'cliente',
    prioridad: 'media',
    presupuesto: '',
    notas: '',
    links: ''
  });

  const handleUnauthorized = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('userEmail');
    window.location.href = '/login';
  };

  const fetchProjects = async () => {
    setLoading(true);
    setError('');
    try {
      const qs = new URLSearchParams();
      if (filters.search) qs.append('search', filters.search);
      if (filters.clientId) qs.append('client_id', filters.clientId);
      if (filters.estado) qs.append('estado', filters.estado);
      if (filters.userId) qs.append('user_id', filters.userId);
      if (filters.fechaInicioDesde) qs.append('fecha_inicio_desde', filters.fechaInicioDesde);
      if (filters.fechaInicioHasta) qs.append('fecha_inicio_hasta', filters.fechaInicioHasta);
      if (filters.fechaFinDesde) qs.append('fecha_fin_desde', filters.fechaFinDesde);
      if (filters.fechaFinHasta) qs.append('fecha_fin_hasta', filters.fechaFinHasta);
      if (sort.field) qs.append('sort', sort.field);
      if (sort.order) qs.append('order', sort.order);
      qs.append('page', page.toString());
      qs.append('pageSize', pageSize.toString());
      const res = await apiFetch(`/api/projects?${qs.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error cargando proyectos');
      const items = data.items || data;
      setProjects(Array.isArray(items) ? items : []);
      setTotal(data.total || items.length || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const all = [];
      let page = 1;
      const requestedPageSize = 100;
      while (true) {
        const res = await apiFetch(`/api/clients?page=${page}&pageSize=${requestedPageSize}`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await parseJsonSafe(res);
        if (res.status === 401) return handleUnauthorized();
        if (!res.ok) break;
        const items = data.data || data.items || [];
        all.push(...items);
        const total = Number(data.total || 0);
        const serverPageSize = Number(data.pageSize || requestedPageSize);
        if (!items.length) break;
        if (total > 0 && all.length >= total) break;
        if (items.length < serverPageSize) break;
        page += 1;
      }
      setClients(all);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await apiFetch('/api/users', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.ok && Array.isArray(data)) setUsers(data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchClients();
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sort, page]);

  const openModal = useCallback((project = null) => {
    setEditing(project);
    setForm({
      titulo: project?.titulo || '',
      client_id: project?.client_id || null,
      estado: project?.estado || 'a_la_espera',
      fecha_inicio: project?.fecha_inicio ? project.fecha_inicio.slice(0, 10) : '',
      fecha_fin: project?.fecha_fin ? project.fecha_fin.slice(0, 10) : '',
      comentarios: project?.comentarios || '',
      assigned_user_ids: Array.isArray(project?.assigned_users)
        ? project.assigned_users.map((u) => u.id)
        : [],
      tipo: project?.tipo || 'cliente',
      prioridad: project?.prioridad || 'media',
      presupuesto: project?.presupuesto || '',
      notas: project?.notas || '',
      links: project?.links || ''
    });
    setModalOpen(true);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const openId = params.get('projectId');
    const shouldOpen = params.get('open') === '1';
    if (!openId || !shouldOpen) return;
    const projectId = Number(openId);
    if (!Number.isFinite(projectId)) return;

    const openWith = async () => {
      const found = projects.find((p) => Number(p.id) === projectId);
      if (found) {
        openModal(found);
        navigate('/proyectos', { replace: true });
        return;
      }
      try {
        const res = await apiFetch(`/api/projects/${projectId}`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await parseJsonSafe(res);
        if (res.status === 401) return handleUnauthorized();
        if (res.ok && data) openModal(data);
      } catch (err) {
        setError(err.message);
      } finally {
        navigate('/proyectos', { replace: true });
      }
    };
    openWith();
  }, [location.search, navigate, openModal, projects, token]);

  const saveProject = async (e) => {
    e.preventDefault();
    if (!form.titulo) {
      setError('El título es obligatorio');
      return;
    }
    try {
      const payload = { ...form };
      const url = editing ? `/api/projects/${editing.id}` : '/api/projects';
      const method = editing ? 'PUT' : 'POST';
      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo guardar el proyecto');
      setModalOpen(false);
      setEditing(null);
      fetchProjects();
    } catch (err) {
      setError(err.message);
    }
  };

  const deleteProject = async (id) => {
    if (!window.confirm('¿Eliminar este proyecto?')) return;
    try {
      const res = await apiFetch(`/api/projects/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo eliminar');
      fetchProjects();
    } catch (err) {
      setError(err.message);
    }
  };

  const clientOptions = clients.map((c) => ({ value: c.id, label: c.razon_social || c.email || `Cliente ${c.id}` }));
  const userOptions = users.map((u) => ({ value: u.id, label: u.name || u.email }));
  const normalize = (str) => (str || '').toString().toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');
  const visibleStatusOptions = useMemo(
    () => (showFinalizados ? statusOptions : statusOptions.filter((s) => s.value !== 'finalizado')),
    [showFinalizados]
  );

  useEffect(() => {
    if (!showFinalizados && filters.estado === 'finalizado') {
      setFilters((p) => ({ ...p, estado: '' }));
    }
  }, [showFinalizados, filters.estado]);

  const filteredProjects = useMemo(() => {
    const searchText = normalize(filters.search);
    return projects
      .filter((p) => {
        if (!showFinalizados && p.estado === 'finalizado') return false;
        if (filters.clientId && String(p.client_id) !== String(filters.clientId)) return false;
        if (filters.estado && p.estado !== filters.estado) return false;
        if (filters.userId && !(p.assigned_users || []).some((u) => String(u.id) === String(filters.userId))) return false;
        if (filters.fechaInicioDesde && p.fecha_inicio && new Date(p.fecha_inicio) < new Date(filters.fechaInicioDesde)) return false;
        if (filters.fechaInicioHasta && p.fecha_inicio && new Date(p.fecha_inicio) > new Date(filters.fechaInicioHasta)) return false;
        if (filters.fechaFinDesde && p.fecha_fin && new Date(p.fecha_fin) < new Date(filters.fechaFinDesde)) return false;
        if (filters.fechaFinHasta && p.fecha_fin && new Date(p.fecha_fin) > new Date(filters.fechaFinHasta)) return false;
        if (searchText) {
          const hay = normalize(p.titulo).includes(searchText) || normalize(p.client_name || '').includes(searchText);
          if (!hay) return false;
        }
        return true;
      })
      .sort((a, b) => {
        const dir = sort.order === 'desc' ? -1 : 1;
        if (sort.field === 'fecha_inicio' || sort.field === 'fecha_fin') {
          const va = a[sort.field] ? new Date(a[sort.field]).getTime() : 0;
          const vb = b[sort.field] ? new Date(b[sort.field]).getTime() : 0;
          return va === vb ? 0 : va > vb ? dir : -dir;
        }
        if (sort.field === 'titulo') {
          return (a.titulo || '').localeCompare(b.titulo || '') * dir;
        }
        return 0;
      });
  }, [projects, filters, sort, showFinalizados]);

  const byEstado = useMemo(() => {
    const map = { a_la_espera: [], en_proceso: [], finalizado: [] };
    filteredProjects.forEach((p) => {
      if (!map[p.estado]) map[p.estado] = [];
      map[p.estado].push(p);
    });
    return map;
  }, [filteredProjects]);

  const timelineData = useMemo(() => {
    const withDates = filteredProjects.map((p) => {
      const start = p.fecha_inicio ? new Date(p.fecha_inicio) : null;
      const end = p.fecha_fin ? new Date(p.fecha_fin) : null;
      return { ...p, start, end, startTs: start ? start.getTime() : null, endTs: end ? end.getTime() : null };
    });
    const valid = withDates.filter((p) => p.startTs || p.endTs);
    if (!valid.length) return { items: [], min: null, max: null };
    const min = Math.min(...valid.map((p) => p.startTs || p.endTs));
    const max = Math.max(...valid.map((p) => p.endTs || p.startTs));
    const span = Math.max(1, max - min);
    const items = valid.map((p) => {
      const startTs = p.startTs || min;
      const endTs = p.endTs || p.startTs || max;
      const left = ((startTs - min) / span) * 100;
      const width = Math.max(2, ((endTs - startTs) / span) * 100);
      return { ...p, left, width };
    });
    return { items, min, max };
  }, [filteredProjects]);

  const quickUpdateEstado = async (project, estado) => {
    try {
      const res = await apiFetch(`/api/projects/${project.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ estado })
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo actualizar estado');
      setProjects((prev) => prev.map((p) => (p.id === project.id ? { ...p, estado } : p)));
    } catch (err) {
      alert(err.message);
    }
  };

  const duplicateProject = async (project) => {
    try {
      const payload = {
        titulo: `${project.titulo} (copia)`,
        client_id: project.client_id,
        estado: 'a_la_espera',
        fecha_inicio: project.fecha_inicio,
        fecha_fin: project.fecha_fin,
        comentarios: project.comentarios,
        assigned_user_ids: (project.assigned_users || []).map((u) => u.id),
        tipo: project.tipo || 'cliente',
        prioridad: project.prioridad || 'media',
        presupuesto: project.presupuesto || null,
        notas: project.notas || '',
        links: project.links || ''
      };
      const res = await apiFetch('/api/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo duplicar');
      fetchProjects();
      alert('Proyecto duplicado');
    } catch (err) {
      alert(err.message);
    }
  };

  const renderBadgesEstado = (estado) => {
    if (estado === 'finalizado') return <span className="badge bg-success">Finalizado</span>;
    if (estado === 'en_proceso') return <span className="badge bg-warning text-dark">En proceso</span>;
    return <span className="badge bg-secondary">A la espera</span>;
  };

  return (
    <div className="small" style={{ fontSize: '0.9rem' }}>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <h2 className="mb-0">Proyectos</h2>
          {previewProject && <QuickModal title={previewProject.titulo || 'Proyecto'} onClose={() => setPreviewProject(null)}>
            <p className="text-muted">{previewProject.client_name || 'Sin cliente'}</p>
            <h6>Comentarios</h6><p style={{ whiteSpace: 'pre-wrap' }}>{previewProject.comentarios || 'Sin comentarios'}</p>
            <h6>Notas</h6><p style={{ whiteSpace: 'pre-wrap' }}>{previewProject.notas || 'Sin notas'}</p>
            <h6>Enlaces</h6>{String(previewProject.links || '').split(/\n/).filter(Boolean).map((link, i) => <div key={i}>{/^https?:\/\/\S+$/.test(link.trim()) ? <a href={link.trim()} target="_blank" rel="noreferrer">{link}</a> : <span style={{ whiteSpace: 'pre-wrap' }}>{link}</span>}</div>)}
            {!previewProject.links && <p>Sin enlaces</p>}
          </QuickModal>}
          <div className="text-muted small">Listas, tablero y fechas clave.</div>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <div className="btn-group">
            <button className={`btn btn-sm btn-outline-secondary ${viewMode === 'lista' ? 'active' : ''}`} onClick={() => setViewMode('lista')}>Lista</button>
            <button className={`btn btn-sm btn-outline-secondary ${viewMode === 'kanban' ? 'active' : ''}`} onClick={() => setViewMode('kanban')}>Kanban</button>
            <button className={`btn btn-sm btn-outline-secondary ${viewMode === 'timeline' ? 'active' : ''}`} onClick={() => setViewMode('timeline')}>Fechas</button>
          </div>
          <button className="btn btn-primary" onClick={() => openModal()}>Nuevo proyecto</button>
        </div>
      </div>
      {error && <div className="alert alert-danger">{error}</div>}
      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="row g-2 align-items-center">
            <div className="col-md-3">
              <input
                className="form-control form-control-sm"
                placeholder="Buscar por título o cliente"
                value={filters.search}
                onChange={(e) => {
                  setFilters((p) => ({ ...p, search: e.target.value }));
                  setPage(1);
                }}
              />
            </div>
            <div className="col-md-3">
              <Select
                classNamePrefix="react-select"
                placeholder="Cliente"
                isClearable
                options={clientOptions}
                value={clientOptions.find((o) => String(o.value) === String(filters.clientId)) || null}
                onChange={(opt) => {
                  setFilters((p) => ({ ...p, clientId: opt ? opt.value : '' }));
                  setPage(1);
                }}
              />
            </div>
            <div className="col-md-2">
              <select
                className="form-select form-select-sm"
                value={filters.estado}
                onChange={(e) => {
                  setFilters((p) => ({ ...p, estado: e.target.value }));
                  setPage(1);
                }}
              >
                <option value="">Estado</option>
                {visibleStatusOptions.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div className="col-md-2">
              <Select
                classNamePrefix="react-select"
                placeholder="Usuario"
                isClearable
                options={userOptions}
                value={userOptions.find((o) => String(o.value) === String(filters.userId)) || null}
                onChange={(opt) => {
                  setFilters((p) => ({ ...p, userId: opt ? opt.value : '' }));
                  setPage(1);
                }}
              />
            </div>
            <div className="col-md-2">
              <select
                className="form-select form-select-sm"
                value={sort.field}
                onChange={(e) => {
                  setSort((p) => ({ ...p, field: e.target.value }));
                  setPage(1);
                }}
              >
                <option value="fecha_fin">Ordenar por fin</option>
                <option value="fecha_inicio">Ordenar por inicio</option>
                <option value="titulo">Ordenar por título</option>
              </select>
            </div>
            <div className="col-md-1">
              <select
                className="form-select form-select-sm"
                value={sort.order}
                onChange={(e) => {
                  setSort((p) => ({ ...p, order: e.target.value }));
                  setPage(1);
                }}
              >
                <option value="asc">Asc</option>
                <option value="desc">Desc</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label small mb-0">Inicio entre</label>
              <div className="d-flex gap-2">
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={filters.fechaInicioDesde}
                  onChange={(e) => {
                    setFilters((p) => ({ ...p, fechaInicioDesde: e.target.value }));
                    setPage(1);
                  }}
                />
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={filters.fechaInicioHasta}
                  onChange={(e) => {
                    setFilters((p) => ({ ...p, fechaInicioHasta: e.target.value }));
                    setPage(1);
                  }}
                />
              </div>
            </div>
            <div className="col-md-3">
              <label className="form-label small mb-0">Fin entre</label>
              <div className="d-flex gap-2">
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={filters.fechaFinDesde}
                  onChange={(e) => {
                    setFilters((p) => ({ ...p, fechaFinDesde: e.target.value }));
                    setPage(1);
                  }}
                />
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={filters.fechaFinHasta}
                  onChange={(e) => {
                    setFilters((p) => ({ ...p, fechaFinHasta: e.target.value }));
                    setPage(1);
                  }}
                />
              </div>
            </div>
            <div className="col-12 d-flex justify-content-end">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="showProjectFinalizados"
                  checked={showFinalizados}
                  onChange={(e) => setShowFinalizados(e.target.checked)}
                />
                <label className="form-check-label small" htmlFor="showProjectFinalizados">
                  Mostrar finalizados
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
      {loading ? (
        <p>Cargando...</p>
      ) : viewMode === 'lista' ? (
        <div className="card shadow-sm">
          <div className="card-body">
            {filteredProjects.length ? (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Título</th>
                      <th>Cliente</th>
                      <th>Estado</th>
                      <th>Inicio</th>
                      <th>Fin</th>
                      <th>Tareas</th>
                      <th>Prioridad</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProjects.map((p) => (
                      <tr key={p.id}>
                        <td>{p.titulo}</td>
                        <td>{p.client_id ? <Link to={`/clientes/${p.client_id}`}>{p.client_name || 'Cliente'}</Link> : (p.client_name || '-')}</td>
                        <td>{renderBadgesEstado(p.estado)}</td>
                        <td>{p.fecha_inicio ? p.fecha_inicio.slice(0, 10) : '-'}</td>
                        <td>
                          <div>{p.fecha_fin ? p.fecha_fin.slice(0, 10) : '-'}</div>
                          {p.fecha_fin && (
                            <span className={`badge ${daysToDate(p.fecha_fin) < 0 ? 'bg-danger' : daysToDate(p.fecha_fin) <= 3 ? 'bg-warning text-dark' : 'bg-light text-dark'}`}>
                              {daysToDate(p.fecha_fin) < 0 ? 'Vencido' : `Faltan ${daysToDate(p.fecha_fin)}d`}
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="small">
                            <div>Abiertas: {p.tasks_abiertas || 0} / {p.tasks_total || 0}</div>
                            <div className="progress" style={{ height: 6 }}>
                              <div
                                className="progress-bar bg-success"
                                role="progressbar"
                                style={{ width: `${p.tasks_total ? ((p.tasks_total - (p.tasks_abiertas || 0)) / p.tasks_total) * 100 : 0}%` }}
                                aria-label="Progreso tareas"
                              ></div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className={`badge ${p.prioridad === 'alta' ? 'bg-danger' : p.prioridad === 'media' ? 'bg-warning text-dark' : 'bg-secondary'}`}>
                            {p.prioridad || 'media'}
                          </span>
                        </td>
                        <td className="text-end">
                          <div className="btn-group">
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => quickUpdateEstado(p, p.estado === 'finalizado' ? 'en_proceso' : 'finalizado')}>
                              {p.estado === 'finalizado' ? 'Reabrir' : 'Finalizar'}
                            </button>
                            <button type="button" className="btn btn-sm btn-outline-secondary" title="Vista rápida del proyecto" aria-label={`Vista rápida de ${p.titulo}`} onClick={() => setPreviewProject(p)}><BsEye /></button> <button className="btn btn-sm btn-outline-primary" onClick={() => navigate(`/proyectos/${p.id}`)}>Editar</button>
                            <button className="btn btn-sm btn-outline-success" onClick={() => duplicateProject(p)}>Duplicar</button>
                            <button className="btn btn-sm btn-outline-danger" onClick={() => deleteProject(p.id)}>Eliminar</button>
                            <Link className="btn btn-sm btn-outline-secondary" to={`/proyectos/${p.id}/tareas`}>Ver tareas</Link>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="d-flex justify-content-between align-items-center mt-2">
                  <div className="small text-muted">Página {page} de {Math.max(1, Math.ceil(total / pageSize))} (total {total})</div>
                  <div className="btn-group">
                    <button className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Anterior</button>
                    <button className="btn btn-sm btn-outline-secondary" disabled={page >= Math.ceil(total / pageSize)} onClick={() => setPage((p) => p + 1)}>Siguiente</button>
                  </div>
                </div>
              </div>
            ) : <p>No hay proyectos.</p>}
          </div>
        </div>
      ) : viewMode === 'kanban' ? (
        <div className="row g-3">
          {visibleStatusOptions.map((col) => (
            <div
              className={visibleStatusOptions.length === 2 ? 'col-md-6' : 'col-md-4'}
              key={col.value}
            >
              <div className="card shadow-sm h-100">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <span>{col.label}</span>
                  <span className="badge bg-light text-dark">{byEstado[col.value]?.length || 0}</span>
                </div>
                <div className="card-body" style={{ minHeight: 150, background: '#f8fafc' }}>
                  {(byEstado[col.value] || []).length ? (
                    (byEstado[col.value] || []).map((p) => (
                      <div key={p.id} className="card mb-2 shadow-sm">
                        <div className="card-body p-2">
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <div className="fw-bold">{p.titulo}</div>
                              <div className="text-muted small">{p.client_id ? <Link to={`/clientes/${p.client_id}`} className="text-decoration-none text-muted" onClick={(e) => e.stopPropagation()}>{p.client_name || 'Cliente'}</Link> : (p.client_name || 'Sin cliente')}</div>
                              <div className="small">
                                <span className={`badge ${p.prioridad === 'alta' ? 'bg-danger' : p.prioridad === 'media' ? 'bg-warning text-dark' : 'bg-secondary'}`}>
                                  {p.prioridad || 'media'}
                                </span>{' '}
                                <span className="badge bg-light text-dark border">{p.tipo || 'cliente'}</span>
                              </div>
                            </div>
                            <div className="btn-group btn-group-sm flex-shrink-0">
                              <button type="button" className="btn btn-sm btn-outline-secondary" title="Vista rápida del proyecto" aria-label={`Vista rápida de ${p.titulo}`} onClick={() => setPreviewProject(p)}><BsEye /></button> <button className="btn btn-outline-secondary" onClick={() => navigate(`/proyectos/${p.id}`)} aria-label="Editar proyecto">✏️</button>
                              <button className="btn btn-outline-danger" onClick={() => deleteProject(p.id)} aria-label="Eliminar proyecto">🗑️</button>
                            </div>
                          </div>
                          <div className="small mt-1">Tareas: {p.tasks_abiertas || 0}/{p.tasks_total || 0}</div>
                          <div className="progress my-1" style={{ height: 4 }}>
                            <div
                              className="progress-bar bg-success"
                              style={{ width: `${p.tasks_total ? ((p.tasks_total - (p.tasks_abiertas || 0)) / p.tasks_total) * 100 : 0}%` }}
                            ></div>
                          </div>
                          <div className="d-flex flex-wrap gap-1 mb-1">
                            {(p.assigned_users || []).map((u) => (
                              <span key={u.id} className="badge rounded-pill bg-light text-dark border">
                                {getInitials(u.name, u.email)}
                              </span>
                            ))}
                          </div>
                          <div className="d-flex justify-content-between align-items-center small mt-2">
                            <div className="text-muted">
                              <div>{p.fecha_inicio ? `Inicio: ${p.fecha_inicio.slice(0,10)}` : 'Sin fecha inicio'}</div>
                              <div>
                                {p.fecha_fin ? `Fin: ${p.fecha_fin.slice(0,10)}` : 'Sin fecha fin'}
                                {p.fecha_fin && (
                                  <span className={`ms-1 badge ${daysToDate(p.fecha_fin) < 0 ? 'bg-danger' : daysToDate(p.fecha_fin) <= 3 ? 'bg-warning text-dark' : 'bg-light text-dark'}`}>
                                    {daysToDate(p.fecha_fin) < 0 ? 'Vencido' : `-${daysToDate(p.fecha_fin)}d`}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="btn-group btn-group-sm">
                              {statusOptions.filter((s) => s.value !== p.estado).map((s) => (
                                <button key={s.value} className="btn btn-light" onClick={() => quickUpdateEstado(p, s.value)}>{s.label}</button>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-muted small">Sin proyectos</div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="row g-3">
          <div className="col-lg-8">
            <div className="card shadow-sm">
              <div className="card-body">
                <h5 className="mb-3">Cronograma</h5>
                {timelineData.items.length ? (
                  <div>
                    <div className="text-muted small mb-1">
                      {timelineData.min ? new Date(timelineData.min).toLocaleDateString() : ''} - {timelineData.max ? new Date(timelineData.max).toLocaleDateString() : ''}
                    </div>
                    <div className="position-relative" style={{ minHeight: 300, background: '#f8fafc', borderRadius: 8, padding: '12px 8px' }}>
                      {timelineData.items.map((p) => (
                        <div key={p.id} className="mb-2">
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="fw-bold small">{p.titulo}</div>
                            <div className="small text-muted">{p.fecha_fin ? p.fecha_fin.slice(0,10) : ''}</div>
                          </div>
                          <div className="position-relative" style={{ height: 14, background: '#e5e7eb', borderRadius: 8 }}>
                            <div
                              className="position-absolute"
                              style={{
                                left: `${p.left}%`,
                                width: `${p.width}%`,
                                top: 0,
                                bottom: 0,
                                background: p.estado === 'finalizado' ? '#22c55e' : p.estado === 'en_proceso' ? '#facc15' : '#cbd5e1',
                                borderRadius: 8
                              }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-muted mb-0">Sin fechas programadas.</p>
                )}
              </div>
            </div>
          </div>
          <div className="col-lg-4">
            <div className="card shadow-sm h-100">
              <div className="card-body">
                <h5 className="mb-3">Estado general</h5>
                <div className="d-flex flex-column gap-2">
                  {statusOptions.map((s) => (
                    <div key={s.value} className="d-flex justify-content-between align-items-center">
                      <span>{s.label}</span>
                      <span className="badge bg-light text-dark">{byEstado[s.value]?.length || 0}</span>
                    </div>
                  ))}
                </div>
                <h6 className="mt-3">Próximos vencimientos</h6>
                <ul className="list-group small">
                  {filteredProjects
                    .filter((p) => p.fecha_fin)
                    .sort((a, b) => new Date(a.fecha_fin) - new Date(b.fecha_fin))
                    .slice(0, 8)
                    .map((p) => (
                      <li key={p.id} className="list-group-item d-flex justify-content-between align-items-center">
                        <div>
                          <div className="fw-bold">{p.titulo}</div>
                          <div className="text-muted">{p.client_id ? <Link to={`/clientes/${p.client_id}`} className="text-decoration-none text-muted">{p.client_name || 'Cliente'}</Link> : (p.client_name || 'Sin cliente')}</div>
                        </div>
                        <div className="text-end">
                          {renderBadgesEstado(p.estado)}<br />
                          <span className="small">{p.fecha_fin?.slice(0,10)}</span>
                        </div>
                      </li>
                    ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {modalOpen && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editing ? 'Editar proyecto' : 'Nuevo proyecto'}</h5>
                <button className="btn-close" onClick={() => setModalOpen(false)}></button>
              </div>
              <form onSubmit={saveProject}>
                <div className="modal-body">
                  <div className="mb-2">
                    <label className="form-label">Título *</label>
                    <input className="form-control" value={form.titulo} onChange={(e) => setForm((p) => ({ ...p, titulo: e.target.value }))} required />
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Cliente</label>
                  <Select
                    options={clientOptions}
                    value={clientOptions.find((o) => o.value === form.client_id) || null}
                    onChange={(opt) => setForm((p) => ({ ...p, client_id: opt ? opt.value : null }))}
                    isClearable
                    placeholder="Selecciona cliente"
                    onInputChange={(val) => {
                      if (val && val.length >= 2) {
                        apiFetch(`/api/clients?page=1&pageSize=50&search=${encodeURIComponent(val)}`, { headers: { Authorization: `Bearer ${token}` } })
                          .then((res) => parseJsonSafe(res).then((data) => ({ res, data })))
                          .then(({ res, data }) => {
                            if (res.ok && data) {
                              const items = data.data || data.items || [];
                              if (items.length) setClients((prev) => {
                                const merged = [...prev];
                                items.forEach((c) => {
                                  if (!merged.some((m) => m.id === c.id)) merged.push(c);
                                });
                                return merged;
                              });
                            }
                          })
                          .catch(() => {});
                      }
                    }}
                  />
                </div>
                  <div className="mb-2">
                    <label className="form-label">Comentarios (opcional)</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={form.comentarios}
                      onChange={(e) => setForm((p) => ({ ...p, comentarios: e.target.value }))}
                      placeholder="Notas internas del proyecto"
                    />
                  </div>
                  <div className="row g-2 mb-2">
                    <div className="col-md-4">
                      <label className="form-label">Tipo</label>
                      <select className="form-select" value={form.tipo} onChange={(e) => setForm((p) => ({ ...p, tipo: e.target.value }))}>
                        {tipoOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Prioridad</label>
                      <select className="form-select" value={form.prioridad} onChange={(e) => setForm((p) => ({ ...p, prioridad: e.target.value }))}>
                        {prioridadOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Presupuesto (€)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="form-control"
                        value={form.presupuesto}
                        onChange={(e) => setForm((p) => ({ ...p, presupuesto: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Usuarios asignados</label>
                    <Select
                      isMulti
                      options={userOptions}
                      value={userOptions.filter((o) => form.assigned_user_ids.includes(o.value))}
                      onChange={(opts) => setForm((p) => ({ ...p, assigned_user_ids: opts ? opts.map((o) => o.value) : [] }))}
                      placeholder="Selecciona uno o varios"
                    />
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Notas rápidas</label>
                    <textarea
                      className="form-control"
                      rows="2"
                      value={form.notas}
                      onChange={(e) => setForm((p) => ({ ...p, notas: e.target.value }))}
                      placeholder="Notas breves"
                    />
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Links / adjuntos</label>
                    <textarea
                      className="form-control"
                      rows="2"
                      value={form.links}
                      onChange={(e) => setForm((p) => ({ ...p, links: e.target.value }))}
                      placeholder="Pega aquí URLs de documentación o adjuntos"
                    />
                  </div>
                  <div className="row g-2 mb-2">
                    <div className="col-6">
                      <label className="form-label">Inicio</label>
                      <input type="date" className="form-control" value={form.fecha_inicio} onChange={(e) => setForm((p) => ({ ...p, fecha_inicio: e.target.value }))} />
                    </div>
                    <div className="col-6">
                      <label className="form-label">Fin</label>
                      <input type="date" className="form-control" value={form.fecha_fin} onChange={(e) => setForm((p) => ({ ...p, fecha_fin: e.target.value }))} />
                    </div>
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Estado</label>
                    <Select
                      options={statusOptions}
                      value={statusOptions.find((o) => o.value === form.estado) || statusOptions[0]}
                      onChange={(opt) => setForm((p) => ({ ...p, estado: opt ? opt.value : 'a_la_espera' }))}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setModalOpen(false)}>Cerrar</button>
                  <button type="submit" className="btn btn-primary">Guardar</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectsPage;
